import { useQuery } from '@tanstack/react-query'
import axios from 'axios'
import { useAccount } from './useAccount'
import type {
  AutoCoderThroughputResponse,
  ThroughputWindowKey,
} from '../types/autocoderThroughput'

export interface UseAutocoderThroughputResult {
  data: AutoCoderThroughputResponse | undefined
  loading: boolean
  error: string | null
  isFetching: boolean
  refetch: () => void
}

/**
 * Shared fetch for the throughput payload.
 *
 * Refresh cadence is NOT a constant here — the server returns pollSeconds for
 * the window it resolved (30s for 60m, 15m for 7d, cut to activePollSeconds —
 * 2s — whenever work is in flight), so the two never drift apart. Polling pauses
 * when the tab is hidden, which is what makes the 2s active cadence affordable.
 *
 * `enabled` exists for the modal case: a tab that is not open must not poll.
 */
const useThroughputQuery = (
  key: unknown[],
  url: string,
  enabled: boolean,
): UseAutocoderThroughputResult => {
  const account = useAccount()
  const sessionId = account.user?.session_info?.sessionId || ''

  const query = useQuery<AutoCoderThroughputResponse, Error>({
    queryKey: key,
    queryFn: async () => {
      const response = await axios.get<AutoCoderThroughputResponse>(url, {
        headers: {
          Accept: 'application/json',
          'x-session-id': sessionId,
        },
      })
      return response.data
    },
    enabled: enabled && !!sessionId,
    // Server decides the cadence; fall back to 60s only before the first
    // successful response tells us what it should be.
    refetchInterval: (q) => (q.state.data?.window.pollSeconds ?? 60) * 1000,
    refetchIntervalInBackground: false,
    // Must stay at or below the fastest cadence the server can ask for
    // (activePollSeconds = 2s). A staleTime longer than the poll interval is
    // incoherent — it claims the data is still fresh at the moment we have
    // already decided to refetch it.
    staleTime: 2 * 1000,
  })

  return {
    data: query.data,
    loading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error ? query.error.message : null,
    refetch: () => {
      void query.refetch()
    },
  }
}

/** The customer-wide admin overview. Realm admins only (the route is /admin). */
export const useAutocoderThroughput = (
  customer: string,
  window: ThroughputWindowKey,
  // The pipeline LIST asks for this payload to feed its per-row queue strips,
  // and most viewers are not realm admins. Rather than poll a route that will
  // 403 forever, the caller says up front whether to ask at all.
  enabled = true,
): UseAutocoderThroughputResult => {
  const params = new URLSearchParams({ window })
  if (customer) params.append('customer', customer)
  return useThroughputQuery(
    ['autocoder-throughput', customer, window],
    `/api/v1/admin/autocoder-throughput?${params.toString()}`,
    enabled,
  )
}

/**
 * The SAME payload for one pipeline, served to anyone who can already see that
 * pipeline. The server scopes every roll-up by pipeline id; this hook is
 * otherwise identical, which is the point — the user-facing view is the admin
 * view filtered, not a smaller reimplementation of it.
 */
export const usePipelineThroughput = (
  customer: string,
  job: string,
  pipelineId: string,
  window: ThroughputWindowKey,
  enabled = true,
): UseAutocoderThroughputResult => {
  const params = new URLSearchParams({ window })
  return useThroughputQuery(
    ['autocoder-pipeline-throughput', customer, job, pipelineId, window],
    `/api/v1/autocoder/${encodeURIComponent(customer)}/${encodeURIComponent(job)}` +
      `/pipelines/${encodeURIComponent(pipelineId)}/throughput?${params.toString()}`,
    enabled && !!customer && !!job && !!pipelineId,
  )
}

export default useAutocoderThroughput
